import React, { useCallback, useContext } from 'react';
import { useDependencies } from 'gw-portals-dependency-react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ModalNextProvider, CheckboxField, IconButton } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { Currency as CurrencyField, ImageComponent } from 'gw-components-platform-react';
import { IntlContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { messages as commonMessages } from 'gw-platform-translations';
import { LobIconUtil } from 'gw-portals-util-js';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import metadata from './InvoicesTable.metadata.json5';
import styles from './InvoicesTable.module.scss';
import messages from './InvoicesTable.messages';

const getProductImage = (invoice) => {
    return (
        <ImageComponent
            src={`/branding/${LobIconUtil.getImageIcon(invoice.offeringCode, invoice.offeringName)}.svg`}
            alt={invoice.offeringName}
            title={invoice.offeringName}
            className={styles.ImageIcon}
        />
    );
};

const renderFormattedCurrencyField = (item, index) => {
    return (
        <div className={styles.currencyContainer}>
            <CurrencyField
                id={`currency_${index}`}
                value={item}
                readOnly
                hideLabel
            />
        </div>
    );
};

const getFormattedCurrency = (item, index, property) => {
    return renderFormattedCurrencyField(item[property.id]);
};

const getCell = (item, index, property) => {
    return item[property.id];
};

function InvoicesTable(props) {
    const {
        invoiceSummary,
        selectedInvoices,
        onUpdateSelectedInvoices,
        showPaidDate,
        showSelect,
        selectedPage,
    } = props;

    const breakpoint = useContext(BreakpointTrackerContext);

    const getPolicyType = useCallback((result) => {
        return _.get(appConfig, `TDICAbbreviations.${result.offeringCode}`, '');
    }, []);

    const intl = useContext(IntlContext);
    const { BillingService: DocumentBillingService } = useDependencies('BillingService');

    const getBillStatus = (item, index, property) => {
        const outstanding = _.get(item, 'amountDue.amount', null);
        if (item.isAdjusted && showPaidDate) {
            return messages.adjusted;
        }
        if (outstanding === 0) {
            return messages.paid;
        }
        return {
            id: `typekey.InvoiceStatus.${item[property.id]}`,
            defaultMessage: item[property.id]
        };
    };

    const getFormattedDate = useCallback((item, index, property) => {
        return intl.formatDate(new Date(item[property.id]), { year: 'numeric', month: 'short', day: 'numeric' });
    }, [intl]);

    const getMiniumPayCheckboxDataCell = useCallback(
        (item, index, property) => {
            const value = item[property.id];
            const companionValue = item[property.companionId];
            const selectedIds = [...selectedInvoices];
            const isChecked = selectedIds.indexOf(value) > -1;
            return (
                <div className={styles.currencyCheckBox}>
                    {breakpoint === 'phone' ? renderFormattedCurrencyField(item[property.amountId]) : ''}
                    <CheckboxField
                        id={`${property.id}_${index}`}
                        name={`${property.id}_${index}`}
                        value={isChecked}
                        className={styles.checkboxStyle}
                        onValueChange={(selected) => {
                            if (selected) {
                                const updated = selectedIds.filter(
                                    (iter) => iter !== companionValue
                                );
                                updated.push(value);
                                onUpdateSelectedInvoices(updated);
                            } else {
                                const updated = selectedIds.filter((iter) => iter !== value);
                                onUpdateSelectedInvoices(updated);
                            }
                        }}
                    />
                    {breakpoint !== 'phone' ? renderFormattedCurrencyField(item[property.amountId]) : ''}
                </div>
            );
        },
        [selectedInvoices, onUpdateSelectedInvoices, breakpoint]
    );

    const getFullPayCheckboxDataCell = useCallback(
        (item, index, property) => {
            const value = item[property.id];
            const selectedIds = [...selectedInvoices];
            const isChecked = selectedIds.indexOf(value) > -1;
            return (
                <div className={styles.currencyCheckBox}>
                    {breakpoint === 'phone' ? renderFormattedCurrencyField(item[property.amountId]) : ''}
                    <CheckboxField
                        id={`${property.id}_${index}`}
                        name={`${property.id}_${index}`}
                        value={isChecked}
                        className={styles.checkboxStyle}
                        onValueChange={(selected) => {
                            if (selected) {
                                const updated = [];
                                selectedIds.forEach((iter) => {
                                    const pol = invoiceSummary.find(
                                        (inv) => iter === inv.invoiceId
                                    );
                                    if ((pol && pol.policyNumber !== value) || !pol) {
                                        updated.push(iter);
                                    }
                                });
                                updated.push(value);
                                onUpdateSelectedInvoices(updated);
                            } else {
                                const updated = selectedIds.filter((iter) => iter !== value);
                                onUpdateSelectedInvoices(updated);
                            }
                        }}
                    />
                    {breakpoint !== 'phone' ? renderFormattedCurrencyField(item[property.amountId]) : ''}
                </div>
            );
        },
        [selectedInvoices, onUpdateSelectedInvoices, invoiceSummary, breakpoint]
    );
    const showDownloadError = useCallback(() => {
        ModalNextProvider.showAlert({
            title: messages.viewDownload,
            message: messages.downloadErrorMessage,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        }).then(_.noop);
    }, []);

    const onViewDownloadClicked = useCallback((document) => {
        const invoiceDocument = _.get(document, 'invoiceDocument');
        if (invoiceDocument) {
            const { publicID, sessionID } = invoiceDocument;
            DocumentBillingService.getBillingDocumentLink(publicID, sessionID)
                .then((response) => {
                    if (response.ok) {
                        response.text().then((text) => {
                            const docWin = window.open('', '_blank');
                            docWin.document.write(text);
                        });
                    } else {
                        showDownloadError();
                    }
                });
        } else {
            showDownloadError();
        }
    }, [DocumentBillingService, showDownloadError]);

    const getViewDownloadCell = useCallback((item) => {
        if (item.invoiceStatus !== 'planned' && _.get(item, 'invoiceDocument.docUID', 'none') !== 'none') {
            return (
                <IconButton icon="mi-insert_drive_file" onClick={() => onViewDownloadClicked(item)} />
            );
        }
        return null;
    }, [onViewDownloadClicked]);

    const maybeSortInvoices = (invoices) => {
        if (showPaidDate) {
            return _.sortBy(invoices, 'lastPaymentDate').reverse();
        }
        return _.sortBy(invoices, ['policyNumber']);
    };

    const overrideProps = {
        tablePaymentScheduleDetail: {
            data: maybeSortInvoices(invoiceSummary)
        },
        tablePaymentScheduleDetailInvoiceNumber: {
            visible: true
        },
        tablePaymentScheduleDetailLastPaymentDate: {
            visible: showPaidDate
        },
        tablePaymentScheduleDetailOutstandingAmount: {
            visible: !showSelect
        },
        tablePaymentScheduleDetailPolicyNumber: {
            visible: showSelect
        },
        tablePaymentScheduleDetailTotalValue: {
            visible: !showSelect
        },
        tablePaymentScheduleDetailPay: {
            visible: showSelect
        },
        tablePaymentPayInFull: {
            visible: showSelect
        },
        tablePaymentScheduleDetailDocuments: {
            visible: !showSelect
        },
        invoiceTypeColumn: {
            onCell: getPolicyType,
            visible: !_.isEmpty(selectedPage)
        },
        invoicePolicyTypeColumn: {
            onCell: getProductImage,
            visible: !_.isEmpty(selectedPage)
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getFormattedDate,
            getBillStatus,
            getFormattedCurrency,
            getCell,
            getMiniumPayCheckboxDataCell,
            getFullPayCheckboxDataCell,
            getViewDownloadCell
        }
    };

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

InvoicesTable.propTypes = {
    invoiceSummary: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectedInvoices: PropTypes.arrayOf(PropTypes.string),
    selectedScreen: PropTypes.string.isRequired,
    onUpdateSelectedInvoices: PropTypes.func,
    showSelect: PropTypes.bool,
    showPaidDate: PropTypes.bool
};

InvoicesTable.defaultProps = {
    selectedInvoices: [],
    onUpdateSelectedInvoices: undefined,
    showSelect: false,
    showPaidDate: false
};

export default InvoicesTable;
